// Pure-data recap computation. Takes raw inputs (visits, clients,
// achievements, lesson states) bounded to a period and returns a structured
// RecapData payload that the UI renders directly and the email trigger
// reformats into HTML.
//
// Kept pure (no DB access) so it's testable, predictable, and the same logic
// runs both client-side (when generating live) and could be ported into a
// pg_cron job later without rewriting.

export type RecapKind = 'weekly' | 'monthly' | 'quarterly' | 'annual';

export type RecapVisit = {
  id: string;
  client_id: string | null;
  cut_type: string | null;
  price_cents: number;
  /** Tip captured on the visit (migration 0065). Optional for back-
   *  compat with callers that haven't been updated yet — treated as 0
   *  when missing. */
  tip_cents?: number;
  /** Default ticket (cut + add-ons at catalog price) recorded at save
   *  time. Drives the discount/upcharge variance metric — sum of
   *  `default_price_cents − price_cents` across visits where the
   *  barber discounted (price < default). Optional for back-compat;
   *  null/undefined treated as equal to price_cents (no discount). */
  default_price_cents?: number | null;
  /** Payment method captured at save (migration 0071). Drives per-
   *  method breakdown for 1099-K reconciliation. Null on historical
   *  visits + on visits where the barber skipped the picker. */
  payment_method?: string | null;
  /** Whether the cut was a pre-booked appointment (true) or a walk-in
   *  (false). Null on historical visits pre-migration 0072 and on
   *  visits where the barber skipped the picker. Drives the walk-in
   *  % surface in the recap's Tax Summary and (eventually) the
   *  forecast's confidence-band widening for high-walk-in barbers. */
  was_appointment?: boolean | null;
  duration_min: number | null;
  started_at: string;
  addons: Array<{ id: string; name: string; priceCents: number }>;
};

export type RecapClient = {
  id: string;
  first_name: string;
  last_name: string;
  created_at: string;
};

export type RecapAchievement = {
  kind: string;
  threshold: number;
  earned_at: string;
};

export type RecapLessonState = {
  lesson_id: string;
  completed: boolean;
  reflection: 'yes' | 'no' | null;
  viewed_at: string | null;
};

export type RecapInput = {
  kind: RecapKind;
  /** YYYY-MM-DD inclusive */
  periodStart: string;
  /** YYYY-MM-DD inclusive */
  periodEnd: string;
  /** Visits within the period (caller filters by started_at). */
  visits: RecapVisit[];
  /** Visits in the equivalent prior period — used for trend %. */
  priorVisits?: RecapVisit[];
  /** All clients (not bounded) — we need their created_at to count "new" within period. */
  clients: RecapClient[];
  /** Achievements earned within the period. */
  milestones: RecapAchievement[];
  /** Lesson states with viewed_at or completed_at within the period. */
  lessonStates: RecapLessonState[];
  /** Snapshot of the streak row at recap time. */
  streak: { current: number; longest: number };
};

/** One row in `sub_period_breakdowns` — a sub-window inside the recap
 *  period. Monthly recaps break down by week; quarterly + annual recaps
 *  break down by month. For tax prep on the annual recap, the monthly
 *  rows are the canonical "income per month" the tax preparer wants. */
export type RecapSubPeriod = {
  /** Display label, e.g. "Apr 1 – Apr 7" / "April" / "Q1 2026". */
  label: string;
  /** Bucket bounds (YYYY-MM-DD inclusive). */
  period_start: string;
  period_end: string;
  /** Cut + add-on revenue for the bucket. */
  revenue_cents: number;
  /** Tip total for the bucket. */
  tips_cents: number;
  /** revenue_cents + tips_cents — the headline "income" figure. */
  total_income_cents: number;
  /** Visit count in the bucket. */
  cuts: number;
  /** Distinct clients served in the bucket (excludes walk-ins
   *  without a client_id). */
  distinct_clients: number;
}

export type RecapData = {
  kind: RecapKind;
  period_start: string;
  period_end: string;
  totals: {
    revenue_cents: number;
    cuts: number;
    new_clients: number;
    chair_time_min: number;
    avg_ticket_cents: number;
    addon_attach_count: number;
    /** Sum of tip_cents across visits in the period. Drives the recap
     *  email tips row + UI tips KPI + (most importantly) the annual
     *  tax-prep summary. 0 for periods where no tip was recorded. */
    tips_cents: number;
    /** Number of visits with a non-zero tip on file. */
    tip_visits: number;
    /** Average tip across visits with a tip — zero-tip visits excluded
     *  from the denominator. Captures generosity-when-tipping rather
     *  than attach rate. 0 when tip_visits = 0. */
    avg_tip_per_tipped_visit_cents: number;
    /** revenue_cents + tips_cents — the headline tax-prep number
     *  ("what the barber actually earned"). Pre-computed so render
     *  paths don't have to sum it each time. */
    total_income_cents: number;
    /** Distinct client_ids served in the period — excludes walk-ins
     *  without a client_id. Different from new_clients (count of
     *  clients whose first visit fell in the period); this counts
     *  every individual served, new or returning. */
    distinct_clients: number;
    /** Sum of (default_price_cents − price_cents) across visits where
     *  the barber discounted at save time (price < default). Negative
     *  variance (upcharges) excluded so this reads as "money left on
     *  the table." Captures the visible cost of discounting for
     *  pricing-coaching analysis. */
    discount_given_cents: number;
    /** Service-only revenue: revenue_cents minus the sum of add-on
     *  prices across visits. Lets reports split "from cuts" vs "from
     *  add-ons" without re-walking the visits. */
    service_revenue_cents: number;
    /** Add-on-only revenue: sum of add-on prices across visits. */
    addon_revenue_cents: number;
    /** Tip rate as a 0-100 percentage: tips_cents / revenue_cents.
     *  Null when revenue is 0 (no denominator). Captures generosity
     *  scaled to ticket size. */
    tip_rate_pct: number | null;
    /** Tip attach rate as a 0-100 percentage: tip_visits / cuts. Null
     *  when there were no cuts. */
    tip_attach_rate_pct: number | null;
    /** Distinct calendar days with at least one visit in the period.
     *  Drives "days worked" + avg_daily_revenue calculations. */
    days_worked: number;
    /** total_income_cents / days_worked (rounded). 0 when no days
     *  worked. The annual recap's most-useful "what does a day earn
     *  me" headline number. */
    avg_daily_income_cents: number;
    /** Hours worked = chair_time_min / 60, rounded to one decimal as
     *  cents-of-an-hour (i.e. tenths × 10 to stay integer). Lets the
     *  caller format as "X.X hours" without floating-point baggage. */
    hours_worked_tenths: number;
    /** Income earned per hour worked: total_income / hours_worked. The
     *  accountant's "effective hourly rate" — a self-employment
     *  earnings benchmark. Null when no hours were worked. */
    earning_rate_cents_per_hour: number | null;
    /** Lowest single-cut price in the period. Surfaces pricing range
     *  for an accountant reviewing transactions. 0 when no cuts. */
    min_ticket_cents: number;
    /** Highest single-cut price in the period (capped at $500 by the
     *  visit-edit guard, but no cap is applied here). 0 when no cuts. */
    max_ticket_cents: number;
    /** Walk-in vs appointment split (migration 0072). Counts visits
     *  where the barber tagged the cut with `was_appointment`; visits
     *  with a NULL value (skipped picker / pre-migration) are
     *  excluded from BOTH numerator and denominator so the ratio
     *  reflects only intentional tagging. Optional for back-compat
     *  with recaps generated before this field shipped — old rows
     *  render the Tax Summary without the line. */
    walkin_count?: number;
    appointment_count?: number;
    /** walkin_count / (walkin_count + appointment_count) × 100. Null
     *  when neither was captured (no intentional tagging in period). */
    walkin_pct?: number | null;
  };
  /** Earliest + latest visit date in the period — the de facto date
   *  range of "business activity" inside the recap window. Empty for
   *  periods with no visits. Useful for an accountant verifying the
   *  reporting window matches the activity. */
  period_activity: {
    first_visit_at: string | null;
    last_visit_at: string | null;
  };
  vs_prior: {
    revenue_change_pct: number | null;
    cuts_change_pct: number | null;
    /** Tip-total % change vs prior period. Null when prior was zero
     *  AND current is non-zero (undefined denominator). */
    tips_change_pct: number | null;
  };
  streak: {
    current: number;
    longest: number;
    /**
     * Longest run of consecutive days within `[period_start, period_end]`
     * on which the user opened (viewed) at least one lesson. Surfaces in
     * the recap modal as the "Streak" KPI — answers "how disciplined were
     * you in this stretch" rather than "what's your all-time number."
     * Falls back to `current` for old recaps stored before this field
     * existed.
     */
    longest_in_period: number;
  };
  lessons: { completed: number; helpful_yes: number; helpful_no: number };
  best_day: { date: string; revenue_cents: number } | null;
  top_cut_types: Array<{ name: string; count: number; revenue_cents: number }>;
  top_addons: Array<{ name: string; count: number; revenue_cents: number }>;
  top_clients: Array<{
    client_id: string;
    name: string;
    visits: number;
    revenue_cents: number;
  }>;
  milestones: Array<{ kind: string; threshold: number; earned_at: string }>;
  /** Per-payment-method breakdown of revenue + tips + cuts in the
   *  period. The accountant's 1099-K reconciliation workbook —
   *  each processor (Square / Venmo / Cash App / Zelle / etc) reports
   *  to the IRS independently, so the barber needs to match their
   *  app-tracked totals against each 1099-K they receive.
   *
   *  Always 7 rows in stable order: cash, card, venmo, zelle, cashapp,
   *  other, unspecified. Rows with zero activity are still emitted
   *  (rendered as 0 / hidden by the UI) so back-compat consumers can
   *  rely on the shape. */
  payment_method_breakdown?: Array<{
    method: 'cash' | 'card' | 'venmo' | 'zelle' | 'cashapp' | 'other' | 'unspecified';
    cuts: number;
    revenue_cents: number;
    tips_cents: number;
    total_income_cents: number;
    /** % of the period's total_income that flowed through this method.
     *  Null when total_income is 0. */
    share_pct: number | null;
  }>;
  /** Sub-period rollup inside the recap window. Shape depends on kind:
   *    weekly   → empty / undefined (no sub-period; the recap IS the
   *               smallest natural unit)
   *    monthly  → 1 row per ISO week inside the month
   *    quarterly→ 1 row per calendar month (3 rows)
   *    annual   → 1 row per calendar month (12 rows, grouped by
   *               quarter when rendered)
   *
   *  Optional for back-compat with recap rows generated before this
   *  field landed. Old rows render without the breakdown section. */
  sub_period_breakdowns?: RecapSubPeriod[];
};

export function computeRecap(input: RecapInput): RecapData {
  const totalsRevenue = sumPrices(input.visits);
  const cuts = input.visits.length;
  const chairTimeMin = input.visits.reduce((s, v) => s + (v.duration_min ?? 0), 0);
  const avgTicket = cuts > 0 ? Math.round(totalsRevenue / cuts) : 0;

  // Tips — total + count of tipped visits + per-tipped-visit average.
  // tip_cents is optional on RecapVisit for back-compat with stale
  // callers; treat missing as 0 here.
  const tipsCents = input.visits.reduce((s, v) => s + (v.tip_cents ?? 0), 0);
  const tipVisits = input.visits.filter((v) => (v.tip_cents ?? 0) > 0).length;
  const avgTipPerTippedVisitCents =
    tipVisits > 0 ? Math.round(tipsCents / tipVisits) : 0;

  // Total income (cuts + tips) — the headline tax-prep number.
  const totalIncomeCents = totalsRevenue + tipsCents;

  // Distinct clients served (excludes walk-ins without a client_id).
  // Different from new_clients (first-visit-in-period count below).
  const distinctClients = new Set(
    input.visits.map((v) => v.client_id).filter((id): id is string => !!id),
  ).size;

  // Discount given — sum of (default_price − price) where positive
  // (discount only, not upcharges). Captures the "money left on the
  // table" by friend-pricing / freebies.
  const discountGivenCents = input.visits.reduce((sum, v) => {
    const def = v.default_price_cents ?? null;
    if (def == null) return sum;
    const delta = def - v.price_cents;
    return delta > 0 ? sum + delta : sum;
  }, 0);

  // Add-on revenue split — sum of all addon priceCents across the
  // period. Service revenue is the rest of the visit revenue.
  const addonRevenueCents = input.visits.reduce(
    (sum, v) => sum + v.addons.reduce((s, a) => s + (a.priceCents ?? 0), 0),
    0,
  );
  const serviceRevenueCents = Math.max(0, totalsRevenue - addonRevenueCents);

  // Tip rate (tips ÷ cut+addon revenue, as %) + tip attach rate.
  const tipRatePct =
    totalsRevenue > 0 ? Math.round((tipsCents / totalsRevenue) * 100) : null;
  const tipAttachRatePct =
    cuts > 0 ? Math.round((tipVisits / cuts) * 100) : null;

  // Days worked — distinct calendar days with at least one visit.
  // Drives avg_daily_income which is the most-useful "what does a
  // working day earn me" headline for the annual recap.
  const workedDays = new Set(input.visits.map((v) => v.started_at.slice(0, 10))).size;
  const avgDailyIncomeCents =
    workedDays > 0 ? Math.round(totalIncomeCents / workedDays) : 0;

  // Hours worked + earning rate. Stored as TENTHS of an hour
  // (integer) so a future migration / db type doesn't need to deal
  // with floats. Render side divides by 10 for display.
  const hoursWorked = chairTimeMin / 60;
  const hoursWorkedTenths = Math.round(hoursWorked * 10);
  const earningRateCentsPerHour =
    hoursWorked > 0 ? Math.round(totalIncomeCents / hoursWorked) : null;

  // Min / max ticket — pricing range across the period.
  let minTicketCents = 0;
  let maxTicketCents = 0;
  if (input.visits.length > 0) {
    minTicketCents = input.visits[0].price_cents;
    maxTicketCents = input.visits[0].price_cents;
    for (const v of input.visits) {
      if (v.price_cents < minTicketCents) minTicketCents = v.price_cents;
      if (v.price_cents > maxTicketCents) maxTicketCents = v.price_cents;
    }
  }

  // Period activity window — first + last visit date. Tax preparers
  // reviewing an annual recap want to see "activity ran from Jan 4
  // through Dec 28" to verify the reporting window matches actual work.
  let firstVisitAt: string | null = null;
  let lastVisitAt: string | null = null;
  for (const v of input.visits) {
    if (firstVisitAt == null || v.started_at < firstVisitAt) firstVisitAt = v.started_at;
    if (lastVisitAt == null || v.started_at > lastVisitAt) lastVisitAt = v.started_at;
  }

  // Walk-in vs appointment counts (migration 0072). Visits with a
  // NULL was_appointment value (skipped picker / pre-migration) are
  // EXCLUDED from both buckets so the % reflects only intentional
  // tagging — a barber who tagged 10 visits as walk-ins and skipped
  // the rest will read "100% walk-in" instead of "10% walk-in," which
  // matches their intent.
  let walkinCount = 0;
  let appointmentCount = 0;
  for (const v of input.visits) {
    if (v.was_appointment === true) appointmentCount += 1;
    else if (v.was_appointment === false) walkinCount += 1;
  }
  const walkinTotalTagged = walkinCount + appointmentCount;
  const walkinPct =
    walkinTotalTagged > 0
      ? Math.round((walkinCount / walkinTotalTagged) * 100)
      : null;

  // Payment method breakdown — 1099-K reconciliation workbook. Group
  // visits by payment_method (null → 'unspecified') and sum revenue
  // + tips + cut count for each. Stable 7-row output so back-compat
  // consumers can rely on the shape.
  type PmKey =
    | 'cash'
    | 'card'
    | 'venmo'
    | 'zelle'
    | 'cashapp'
    | 'other'
    | 'unspecified';
  const PM_ORDER: PmKey[] = ['cash', 'card', 'venmo', 'zelle', 'cashapp', 'other', 'unspecified'];
  const pmAgg: Record<PmKey, { cuts: number; revenue: number; tips: number }> = {
    cash:        { cuts: 0, revenue: 0, tips: 0 },
    card:        { cuts: 0, revenue: 0, tips: 0 },
    venmo:       { cuts: 0, revenue: 0, tips: 0 },
    zelle:       { cuts: 0, revenue: 0, tips: 0 },
    cashapp:     { cuts: 0, revenue: 0, tips: 0 },
    other:       { cuts: 0, revenue: 0, tips: 0 },
    unspecified: { cuts: 0, revenue: 0, tips: 0 },
  };
  for (const v of input.visits) {
    const raw = (v.payment_method ?? '').toLowerCase();
    const key: PmKey = (
      raw && PM_ORDER.includes(raw as PmKey) ? raw : 'unspecified'
    ) as PmKey;
    pmAgg[key].cuts += 1;
    pmAgg[key].revenue += v.price_cents ?? 0;
    pmAgg[key].tips += v.tip_cents ?? 0;
  }
  const paymentMethodBreakdown = PM_ORDER.map((key) => {
    const acc = pmAgg[key];
    const totalIncome = acc.revenue + acc.tips;
    const sharePct =
      totalIncomeCents > 0
        ? Math.round((totalIncome / totalIncomeCents) * 100)
        : null;
    return {
      method: key,
      cuts: acc.cuts,
      revenue_cents: acc.revenue,
      tips_cents: acc.tips,
      total_income_cents: totalIncome,
      share_pct: sharePct,
    };
  });

  // New clients — created_at within the period.
  const newClientCount = input.clients.filter((c) => {
    const created = c.created_at.slice(0, 10);
    return created >= input.periodStart && created <= input.periodEnd;
  }).length;

  // Add-on attach: count of visits that had at least one addon.
  const addonAttachCount = input.visits.filter((v) => v.addons.length > 0).length;

  // Trend vs prior period — revenue, cuts, AND tips. Tips landed in
  // RecapData v2 so back-compat callers may see null tips_change_pct
  // (prior had no tip data); the render side falls back gracefully.
  const priorRevenue = sumPrices(input.priorVisits ?? []);
  const priorCuts = (input.priorVisits ?? []).length;
  const priorTips = (input.priorVisits ?? []).reduce(
    (s, v) => s + (v.tip_cents ?? 0),
    0,
  );
  const vsPrior = {
    revenue_change_pct: pctChange(priorRevenue, totalsRevenue),
    cuts_change_pct: pctChange(priorCuts, cuts),
    tips_change_pct: pctChange(priorTips, tipsCents),
  };

  // Best day = day with highest revenue.
  const byDay = new Map<string, number>();
  for (const v of input.visits) {
    const day = v.started_at.slice(0, 10);
    byDay.set(day, (byDay.get(day) ?? 0) + v.price_cents);
  }
  let bestDay: RecapData['best_day'] = null;
  for (const [date, rev] of byDay) {
    if (!bestDay || rev > bestDay.revenue_cents) bestDay = { date, revenue_cents: rev };
  }

  // Top cut types — group by cut_type string, ignore the "+ addon list" tail.
  const cutTypeRollup = new Map<string, { count: number; revenue: number }>();
  for (const v of input.visits) {
    const name = (v.cut_type ?? 'Walk-in').split(' + ')[0]?.trim() || 'Walk-in';
    const cur = cutTypeRollup.get(name) ?? { count: 0, revenue: 0 };
    cur.count += 1;
    cur.revenue += v.price_cents;
    cutTypeRollup.set(name, cur);
  }
  // Cap at 10 (was 3 in v1) — annual recaps for tax prep benefit from
  // a fuller service-line breakdown; weekly recaps will naturally have
  // fewer distinct services so the render-side limit handles itself.
  const topCutTypes = [...cutTypeRollup.entries()]
    .map(([name, v]) => ({ name, count: v.count, revenue_cents: v.revenue }))
    .sort((a, b) => b.count - a.count || b.revenue_cents - a.revenue_cents)
    .slice(0, 10);

  // Top add-ons.
  const addonRollup = new Map<string, { count: number; revenue: number }>();
  for (const v of input.visits) {
    for (const a of v.addons) {
      const cur = addonRollup.get(a.name) ?? { count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += a.priceCents ?? 0;
      addonRollup.set(a.name, cur);
    }
  }
  const topAddons = [...addonRollup.entries()]
    .map(([name, v]) => ({ name, count: v.count, revenue_cents: v.revenue }))
    .sort((a, b) => b.count - a.count || b.revenue_cents - a.revenue_cents)
    .slice(0, 10);

  // Top clients (by revenue in period).
  const clientById = new Map(input.clients.map((c) => [c.id, c]));
  const clientRollup = new Map<string, { count: number; revenue: number }>();
  for (const v of input.visits) {
    if (!v.client_id) continue;
    const cur = clientRollup.get(v.client_id) ?? { count: 0, revenue: 0 };
    cur.count += 1;
    cur.revenue += v.price_cents;
    clientRollup.set(v.client_id, cur);
  }
  // Cap at 10 for top clients (was 5 in v1). Annual recaps benefit
  // from a deeper VIP list; render side can trim further per surface.
  const topClients = [...clientRollup.entries()]
    .map(([client_id, v]) => {
      const c = clientById.get(client_id);
      const name = c ? `${c.first_name} ${c.last_name}`.trim() || 'Client' : 'Client';
      return { client_id, name, visits: v.count, revenue_cents: v.revenue };
    })
    .sort((a, b) => b.revenue_cents - a.revenue_cents)
    .slice(0, 10);

  // Longest in-period streak — distinct days within [periodStart, periodEnd]
  // that the user viewed at least one lesson, then the longest contiguous
  // run. Lesson views are the gating event for the daily streak (mirrors
  // what check_streak() considers a "kept" day server-side).
  const viewedDaysInPeriod = new Set<string>();
  for (const s of input.lessonStates) {
    if (!s.viewed_at) continue;
    const day = s.viewed_at.slice(0, 10);
    if (day >= input.periodStart && day <= input.periodEnd) {
      viewedDaysInPeriod.add(day);
    }
  }
  const longestInPeriod = longestConsecutiveRun([...viewedDaysInPeriod].sort());

  // Lessons.
  const completed = input.lessonStates.filter((s) => s.completed).length;
  const helpful = input.lessonStates.reduce(
    (acc, s) => {
      if (s.reflection === 'yes') acc.yes += 1;
      else if (s.reflection === 'no') acc.no += 1;
      return acc;
    },
    { yes: 0, no: 0 },
  );

  // ─── Sub-period breakdowns ─────────────────────────────────────────
  // monthly  → weekly buckets (one per ISO week inside the month)
  // quarterly → monthly buckets (3 rows)
  // annual   → monthly buckets (12 rows — render side can group by quarter)
  // weekly   → undefined (the recap IS the smallest natural unit)
  const subPeriodBreakdowns: RecapSubPeriod[] | undefined =
    input.kind === 'weekly'
      ? undefined
      : computeSubPeriodBreakdowns(
          input.visits,
          input.periodStart,
          input.periodEnd,
          input.kind === 'monthly' ? 'week' : 'month',
        );

  return {
    kind: input.kind,
    period_start: input.periodStart,
    period_end: input.periodEnd,
    totals: {
      revenue_cents: totalsRevenue,
      cuts,
      new_clients: newClientCount,
      chair_time_min: chairTimeMin,
      avg_ticket_cents: avgTicket,
      addon_attach_count: addonAttachCount,
      tips_cents: tipsCents,
      tip_visits: tipVisits,
      avg_tip_per_tipped_visit_cents: avgTipPerTippedVisitCents,
      total_income_cents: totalIncomeCents,
      distinct_clients: distinctClients,
      discount_given_cents: discountGivenCents,
      service_revenue_cents: serviceRevenueCents,
      addon_revenue_cents: addonRevenueCents,
      tip_rate_pct: tipRatePct,
      tip_attach_rate_pct: tipAttachRatePct,
      days_worked: workedDays,
      avg_daily_income_cents: avgDailyIncomeCents,
      hours_worked_tenths: hoursWorkedTenths,
      earning_rate_cents_per_hour: earningRateCentsPerHour,
      min_ticket_cents: minTicketCents,
      max_ticket_cents: maxTicketCents,
      walkin_count: walkinCount,
      appointment_count: appointmentCount,
      walkin_pct: walkinPct,
    },
    period_activity: {
      first_visit_at: firstVisitAt,
      last_visit_at: lastVisitAt,
    },
    payment_method_breakdown: paymentMethodBreakdown,
    vs_prior: vsPrior,
    streak: { ...input.streak, longest_in_period: longestInPeriod },
    lessons: { completed, helpful_yes: helpful.yes, helpful_no: helpful.no },
    best_day: bestDay,
    top_cut_types: topCutTypes,
    top_addons: topAddons,
    top_clients: topClients,
    milestones: input.milestones.map((m) => ({
      kind: m.kind,
      threshold: m.threshold,
      earned_at: m.earned_at,
    })),
    sub_period_breakdowns: subPeriodBreakdowns,
  };
}

/** Group visits into either ISO-week or calendar-month buckets within
 *  the recap period. Returns one RecapSubPeriod per bucket in
 *  chronological order, with totals pre-computed.
 *
 *  Bucket logic:
 *    'week':  ISO week (Mon-Sun). Buckets are clipped at the recap's
 *             period_start / period_end so a partial first/last week
 *             of the month renders with the right label range.
 *    'month': Calendar month — bucket per YYYY-MM. Always whole months
 *             since the recap's period_start/end land on month bounds
 *             for quarterly + annual recaps. */
function computeSubPeriodBreakdowns(
  visits: RecapVisit[],
  periodStart: string,
  periodEnd: string,
  granularity: 'week' | 'month',
): RecapSubPeriod[] {
  // Bucket key → accumulator. Map preserves insertion order so we just
  // pre-seed the buckets in chronological order, then walk visits.
  type Acc = {
    label: string;
    period_start: string;
    period_end: string;
    revenue: number;
    tips: number;
    cuts: number;
    clients: Set<string>;
  };
  const buckets = new Map<string, Acc>();

  const start = new Date(periodStart + 'T00:00:00');
  const end = new Date(periodEnd + 'T00:00:00');

  if (granularity === 'week') {
    // Walk ISO weeks. Start at the Monday on or before period_start.
    const cursor = mondayOnOrBefore(start);
    while (cursor <= end) {
      const weekStart = new Date(cursor);
      const weekEnd = new Date(cursor);
      weekEnd.setDate(weekEnd.getDate() + 6);
      // Clip to period bounds so a partial first/last week reads
      // correctly ("Apr 28 – Apr 30" not "Apr 28 – May 4" when the
      // monthly recap is for April).
      const clippedStart = weekStart < start ? new Date(start) : weekStart;
      const clippedEnd = weekEnd > end ? new Date(end) : weekEnd;
      const key = ymd(weekStart);
      buckets.set(key, {
        label: formatWeekLabel(clippedStart, clippedEnd),
        period_start: ymd(clippedStart),
        period_end: ymd(clippedEnd),
        revenue: 0,
        tips: 0,
        cuts: 0,
        clients: new Set<string>(),
      });
      cursor.setDate(cursor.getDate() + 7);
    }
  } else {
    // Walk months between start and end.
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      const mStart = new Date(cursor);
      const mEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0); // day 0 of next month = last day of this
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      buckets.set(key, {
        label: formatMonthLabel(mStart),
        period_start: ymd(mStart),
        period_end: ymd(mEnd),
        revenue: 0,
        tips: 0,
        cuts: 0,
        clients: new Set<string>(),
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }

  // Walk visits, drop each into its bucket.
  for (const v of visits) {
    const day = v.started_at.slice(0, 10);
    let key: string;
    if (granularity === 'week') {
      const d = new Date(day + 'T00:00:00');
      key = ymd(mondayOnOrBefore(d));
    } else {
      key = day.slice(0, 7); // YYYY-MM
    }
    const acc = buckets.get(key);
    if (!acc) continue; // visit outside period bounds — shouldn't happen but defensive
    acc.revenue += v.price_cents;
    acc.tips += v.tip_cents ?? 0;
    acc.cuts += 1;
    if (v.client_id) acc.clients.add(v.client_id);
  }

  return [...buckets.values()].map((acc) => ({
    label: acc.label,
    period_start: acc.period_start,
    period_end: acc.period_end,
    revenue_cents: acc.revenue,
    tips_cents: acc.tips,
    total_income_cents: acc.revenue + acc.tips,
    cuts: acc.cuts,
    distinct_clients: acc.clients.size,
  }));
}

function mondayOnOrBefore(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0 = Sunday
  const offset = dow === 0 ? 6 : dow - 1;
  x.setDate(x.getDate() - offset);
  return x;
}

function formatWeekLabel(start: Date, end: Date): string {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  if (start.getMonth() === end.getMonth()) {
    return `${fmt.format(start)} – ${end.getDate()}`;
  }
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

function formatMonthLabel(d: Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d);
}

function sumPrices(visits: RecapVisit[]): number {
  return visits.reduce((s, v) => s + (v.price_cents ?? 0), 0);
}

function pctChange(prior: number, current: number): number | null {
  if (prior === 0) return current === 0 ? 0 : null;
  return Math.round(((current - prior) / prior) * 100);
}

/** Given an ascending list of YYYY-MM-DD strings, returns the length of
 *  the longest run where each day is exactly one calendar day after the
 *  previous. Empty input → 0. Used by the recap to compute the period's
 *  longest in-window streak from lesson view-days. */
function longestConsecutiveRun(sortedDays: string[]): number {
  if (sortedDays.length === 0) return 0;
  let best = 1;
  let cur = 1;
  for (let i = 1; i < sortedDays.length; i++) {
    const a = new Date(sortedDays[i - 1] + 'T00:00:00Z').getTime();
    const b = new Date(sortedDays[i] + 'T00:00:00Z').getTime();
    const days = Math.round((b - a) / 86400000);
    if (days === 1) {
      cur += 1;
      if (cur > best) best = cur;
    } else {
      cur = 1;
    }
  }
  return best;
}

/* ============================================================================
 * Period helpers — the dashboard caller uses these to compute period bounds
 * for the current week / current month / prior week / prior month.
 * ============================================================================ */

export function lastCompletedWeekBounds(now = new Date()): { start: string; end: string } {
  // ISO weeks (Monday-Sunday). Roll back to last Monday → its preceding
  // Monday is the start, the next Sunday is the end.
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  // 0 = Sunday; offsetToMonday = 1..7 days back to Monday OF the current week
  const dow = d.getDay();
  const offsetToMon = dow === 0 ? 6 : dow - 1;
  // Start of THIS week's Monday:
  const thisMon = new Date(d);
  thisMon.setDate(d.getDate() - offsetToMon);
  // Last completed week = previous Mon..Sun
  const lastMon = new Date(thisMon);
  lastMon.setDate(thisMon.getDate() - 7);
  const lastSun = new Date(lastMon);
  lastSun.setDate(lastMon.getDate() + 6);
  return { start: ymd(lastMon), end: ymd(lastSun) };
}

export function lastCompletedMonthBounds(now = new Date()): { start: string; end: string } {
  // Previous calendar month: first → last.
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfPrevMonth = new Date(firstOfThisMonth);
  lastOfPrevMonth.setDate(0); // day 0 of "this month" = last day of prev month
  const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
  return { start: ymd(firstOfPrevMonth), end: ymd(lastOfPrevMonth) };
}

/**
 * Previous calendar quarter (Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec).
 * "Last completed" means today is at least one day past the quarter end —
 * e.g. on April 1st you get Q1 (Jan 1 - Mar 31), on March 31st you get
 * Q4 of the prior year. Mirrors lastCompletedWeek/Month semantics.
 */
export function lastCompletedQuarterBounds(now = new Date()): {
  start: string;
  end: string;
} {
  // Current quarter index (0..3) based on the month.
  const currentQ = Math.floor(now.getMonth() / 3);
  const currentYear = now.getFullYear();
  // Wrap to Q4 of prior year if we're still in Q1 of this year.
  const prevQ = currentQ === 0 ? 3 : currentQ - 1;
  const prevYear = currentQ === 0 ? currentYear - 1 : currentYear;
  const startMonth = prevQ * 3;
  const start = new Date(prevYear, startMonth, 1);
  // End = day before the start of the NEXT quarter.
  const endExclusive = new Date(prevYear, startMonth + 3, 1);
  const end = new Date(endExclusive.getTime() - 86400000);
  return { start: ymd(start), end: ymd(end) };
}

/**
 * Previous calendar year (Jan 1 - Dec 31 of last year). Drives the
 * annual recap that doubles as the tax-prep summary — barbers need
 * total revenue + total tips for the year, captured in one row.
 */
export function lastCompletedYearBounds(now = new Date()): {
  start: string;
  end: string;
} {
  const prevYear = now.getFullYear() - 1;
  return {
    start: ymd(new Date(prevYear, 0, 1)),
    end: ymd(new Date(prevYear, 11, 31)),
  };
}

export function priorPeriodBounds(
  start: string,
  end: string,
): { start: string; end: string } {
  // Same length, immediately preceding.
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const lengthMs = e.getTime() - s.getTime() + 86400000;
  const priorEnd = new Date(s.getTime() - 86400000);
  const priorStart = new Date(priorEnd.getTime() - lengthMs + 86400000);
  return { start: ymd(priorStart), end: ymd(priorEnd) };
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
